/**
 * Country Card Component - Display country information in a card
 */

import React from 'react';
import {
  GraduationCap, MapPin, DollarSign, Shield, Heart,
  Briefcase, Globe, TrendingUp, Home
} from 'lucide-react';
import type { Country } from '../types';

interface CountryCardProps {
  country: Country;
  onClick: () => void;
}

export const CountryCard: React.FC<CountryCardProps> = ({ country, onClick }) => {
  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return `$${(amount / 1000).toFixed(0)}k`;
  };

  const formatTemperature = (temp?: number) => {
    if (temp === undefined) return null;
    return `${temp}°C`;
  };

  return (
    <div
      onClick={onClick}
      className="bg-card rounded-2xl p-6 border border-border hover:shadow-lg hover:border-success/30 transition-all cursor-pointer group"
    >
      {/* Country Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {country.flag_emoji && (
            <span className="text-4xl leading-none">{country.flag_emoji}</span>
          )}
          <div>
            <h3 className="text-xl font-bold text-foreground group-hover:text-success transition-colors">
              {country.name}
            </h3>
            {country.region && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                <MapPin className="h-3.5 w-3.5" />
                {country.region}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Universities */}
        <div className="bg-muted/50 rounded-xl p-3">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <GraduationCap className="h-4 w-4" />
            <span className="text-xs font-medium">Universities</span>
          </div>
          <div className="text-lg font-bold text-foreground">
            {country.universities_count || country.total_universities || 'N/A'}
          </div>
        </div>

        {/* Safety Index */}
        {country.safety_index !== null && country.safety_index !== undefined && (
          <div className="bg-muted/50 rounded-xl p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Shield className="h-4 w-4" />
              <span className="text-xs font-medium">Safety</span>
            </div>
            <div className="text-lg font-bold text-foreground">
              {typeof country.safety_index === 'number' ? country.safety_index.toFixed(1) : country.safety_index}/100
            </div>
          </div>
        )}

        {/* Living Cost */}
        {country.avg_living_cost_usd_yearly && (
          <div className="bg-muted/50 rounded-xl p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Home className="h-4 w-4" />
              <span className="text-xs font-medium">Living Cost/Year</span>
            </div>
            <div className="text-lg font-bold text-foreground">
              {formatCurrency(country.avg_living_cost_usd_yearly)}
            </div>
          </div>
        )}

        {/* Tuition Range */}
        {country.avg_tuition_undergrad_usd_min && (
          <div className="bg-muted/50 rounded-xl p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium">Tuition Range</span>
            </div>
            <div className="text-sm font-bold text-foreground">
              {formatCurrency(country.avg_tuition_undergrad_usd_min)} - {formatCurrency(country.avg_tuition_undergrad_usd_max)}
            </div>
          </div>
        )}
      </div>

      {/* Key Features */}
      <div className="flex flex-wrap gap-2 mb-4">
        {country.part_time_work_allowed && (
          <span className="px-3 py-1.5 bg-success/10 text-success rounded-full text-xs font-medium flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5" />
            Part-time Work
          </span>
        )}
        {country.post_study_work_visa_months && country.post_study_work_visa_months > 0 && (
          <span className="px-3 py-1.5 bg-info/10 text-info rounded-full text-xs font-medium flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            {country.post_study_work_visa_months}mo Work Visa
          </span>
        )}
        {country.permanent_residency_possible && (
          <span className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-medium flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5" />
            PR Pathway
          </span>
        )}
        {typeof country.quality_of_life_score === 'number' && country.quality_of_life_score > 7 && (
          <span className="px-3 py-1.5 bg-accent/50 text-accent-foreground rounded-full text-xs font-medium flex items-center gap-1.5">
            <Heart className="h-3.5 w-3.5" />
            High Quality of Life
          </span>
        )}
      </div>

      {/* Additional Info */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
        <div className="flex items-center gap-4">
          {country.primary_language && (
            <span>Language: {country.primary_language}</span>
          )}
          {country.climate_type && (
            <span>Climate: {country.climate_type}</span>
          )}
        </div>
        {(country.avg_temp_summer_c || country.avg_temp_winter_c) && (
          <div className="flex items-center gap-2">
            {country.avg_temp_summer_c && <span>{formatTemperature(country.avg_temp_summer_c)} summer</span>}
            {country.avg_temp_winter_c && <span>{formatTemperature(country.avg_temp_winter_c)} winter</span>}
          </div>
        )}
      </div>
    </div>
  );
};
